import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Button,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  TablePagination,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import GroupIcon from "@mui/icons-material/Group";
import { useNavigate } from "react-router-dom";
import { getAgencies } from "../../api/auth";
import DashboardLayout from "../../layout/DashboardLayout";
import { useAuth } from "../../context/AuthContext";
import { ROLES } from "../../constants/roles";
import { EmptyState } from "../../components/EmptyState";

const kycChipProps = (status) => {
  switch (status) {
    case "Submitted":
      return { label: "Submitted", color: "success" };
    case "Pending":
      return { label: "Pending", color: "warning" };
    case "Completed":
      return { label: "Completed", color: "success" };
    default:
      return { label: status || "Not Started", color: "default" };
  }
};

export default function AgenciesPage() {
  const { user } = useAuth();
  const isSpecialist = user?.role === ROLES.SALES_ACCOUNT_SPECIALIST;
  const [agencies, setAgencies] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(isSpecialist ? "mine" : "all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await getAgencies();
        setAgencies(res.data);
      } catch (err) {
        console.error("Failed to fetch agencies:", err);
        setError("Failed to load agencies. Please refresh the page and try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading)
    return (
      <DashboardLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );

  if (error)
    return (
      <DashboardLayout>
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      </DashboardLayout>
    );

  const unassignedCount = agencies.filter((a) => !a.assignedSpecialist).length;
  const myAgenciesCount = agencies.filter((a) => a.assignedSpecialist?._id === user?.id).length;

  const afterFilter = agencies.filter((a) => {
    if (filter === "unassigned") return !a.assignedSpecialist;
    if (filter === "mine") return a.assignedSpecialist?._id === user?.id;
    return true;
  });

  const filtered = afterFilter.filter(
    (a) =>
      !search ||
      a.name?.toLowerCase().includes(search.toLowerCase()) ||
      a.owner?.toLowerCase().includes(search.toLowerCase()) ||
      a.country?.toLowerCase().includes(search.toLowerCase()) ||
      a.state?.toLowerCase().includes(search.toLowerCase()) ||
      a.city?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <Box mb={4}>
        <Typography variant="h5" sx={{ color: "#0f172a" }}>
          Registered Agencies
        </Typography>
        <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
          {agencies.length} agenc{agencies.length === 1 ? "y" : "ies"} registered
          {unassignedCount > 0 && (
            <Box component="span" sx={{ ml: 1.5 }}>
              <Chip
                label={`${unassignedCount} unassigned`}
                color="warning"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            </Box>
          )}
        </Typography>
      </Box>

      <Paper>
        <Box px={3} py={2} sx={{ borderBottom: "1px solid #f1f5f9", display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            placeholder="Search by name, owner, country or state…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            size="small"
            sx={{ width: { xs: "100%", sm: 300 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "1rem", color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
          />
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={(_, val) => { if (val) { setFilter(val); setPage(0); } }}
            size="small"
          >
            {isSpecialist && (
              <ToggleButton value="mine">
                My Agencies
                {myAgenciesCount > 0 && (
                  <Box
                    component="span"
                    sx={{
                      ml: 0.75,
                      bgcolor: "#0284c7",
                      color: "#fff",
                      borderRadius: "10px",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      px: 0.75,
                      py: 0.1,
                    }}
                  >
                    {myAgenciesCount}
                  </Box>
                )}
              </ToggleButton>
            )}
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="unassigned">
              Unassigned
              {unassignedCount > 0 && (
                <Box
                  component="span"
                  sx={{
                    ml: 0.75,
                    bgcolor: "#f59e0b",
                    color: "#fff",
                    borderRadius: "10px",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    px: 0.75,
                    py: 0.1,
                  }}
                >
                  {unassignedCount}
                </Box>
              )}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Agency</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>KYC Status</TableCell>
                <TableCell>Assignment</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ border: 0 }}>
                    <EmptyState
                      variant="compact"
                      icon={<GroupIcon sx={{ fontSize: "1.5rem", color: "#cbd5e1" }} />}
                      description={
                        agencies.length === 0
                          ? "No agencies registered yet."
                          : search
                            ? "No agencies match your search."
                            : "No agencies match this filter."
                      }
                      action={
                        agencies.length > 0
                          ? {
                              label: search ? "Clear search" : "Clear filter",
                              onClick: () => {
                                setSearch("");
                                setFilter("all");
                                setPage(0);
                              },
                            }
                          : undefined
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : (
                filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((agency) => {
                  const initials = agency.name
                    ?.split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2) || "?";
                  const chip = kycChipProps(agency.kycStatus);
                  const hasAssignment = agency.assignedSpecialist;

                  return (
                    <TableRow key={agency._id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Avatar sx={{ width: 34, height: 34, fontSize: "0.75rem", bgcolor: "#0284c7", fontWeight: 700 }}>
                            {initials}
                          </Avatar>
                          <Typography variant="body2" fontWeight={600} color="#0f172a">
                            {agency.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="#475569">
                          {[agency.city, agency.state, agency.country].filter(Boolean).join(", ")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500} color="#0f172a">
                          {agency.owner}
                        </Typography>
                        <Typography variant="caption" color="#94a3b8">
                          {agency.ownerEmail}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={chip.label}
                          color={chip.color}
                          size="small"
                          sx={chip.color === "default" ? { bgcolor: "#f1f5f9", color: "#64748b" } : {}}
                        />
                      </TableCell>
                      <TableCell>
                        {hasAssignment ? (
                          <Box>
                            <Typography variant="caption" color="#0f172a" display="block" fontWeight={500}>
                              {agency.assignedSpecialist.name}
                            </Typography>
                          </Box>
                        ) : (
                          <Chip label="Unassigned" size="small" sx={{ bgcolor: "#fef3c7", color: "#92400e", fontWeight: 600 }} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button variant="outlined" size="small" onClick={() => navigate(`/agencies/${agency._id}`)}>
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>
    </DashboardLayout>
  );
}